import { clientDataFromReviewFields } from '../../utils/fundingClientIntakeUtils';
import {
  isNoisyExtractedAddress,
  isNoisyExtractedName,
  maskSensitiveExtractedValue,
} from '../../utils/fundingClientIntakeUtils';
import {
  EXTRACTION_PARTIAL_MANUAL_HINT,
  EXTRACTION_PARTIAL_TEXT_ONLY_MESSAGE,
  EXTRACTION_PARTIAL_TITLE,
  EXTRACTION_STATUS,
} from './documentExtractionService';

const API_BASE = '/api/funding-hq';
const PARSE_TEXT_PATH = `${API_BASE}/documents/parse-text`;
const PASTED_SOURCE = 'Pasted client info';

function getAuthToken() {
  return (
    localStorage.getItem('fundelite_token')
    || localStorage.getItem('token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('authToken')
  );
}

function backendBaseUrl() {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  return raw.replace(/\/api$/, '');
}

export const PASTE_PARTIAL_TEXT_ONLY_MESSAGE =
  'We found pasted text but could not confidently place it into fields.';

function normalizeReviewField(entry, index) {
  const label = entry.label || entry.field || entry.fieldKey || 'Field';
  if (label === 'Filing Type') {
    return null;
  }
  let extracted = entry.value ?? entry.extracted ?? '';
  if (entry.sensitive || label === 'SSN' || label === 'EIN') {
    extracted = maskSensitiveExtractedValue(label, extracted);
  }
  if (label === 'Personal Address' && isNoisyExtractedAddress(extracted)) {
    extracted = '';
  }
  if (['Owner Name', 'First Name', 'Last Name'].includes(label) && isNoisyExtractedName(extracted)) {
    extracted = '';
  }
  if (!extracted) {
    return null;
  }
  return {
    id: entry.id || `field_${index + 1}`,
    field: label,
    fieldKey: entry.fieldKey || null,
    category: entry.category || 'Identity',
    current: '—',
    extracted,
    source: entry.sourceDocument || entry.source || PASTED_SOURCE,
    documentTypeDetected: entry.documentTypeDetected || entry.document_type_detected || 'client_profile_text',
    confidence: entry.confidence ?? null,
    sensitive: Boolean(entry.sensitive),
    apply: entry.apply !== false && Boolean(extracted),
  };
}

function mapParseStatus(doc) {
  if (doc.status === 'failed' || doc.status === 'error') {
    return EXTRACTION_STATUS.ERROR;
  }
  if (doc.status === 'partial') {
    return EXTRACTION_STATUS.PARTIAL;
  }
  return EXTRACTION_STATUS.EXTRACTED;
}

export function normalizePastedParseResult(data, pastedText = '') {
  const doc = data?.documents?.[0] || {};
  const fields = [];
  (doc.extractedFields || []).forEach((field) => {
    const normalized = normalizeReviewField({
      ...field,
      sourceDocument: field.sourceDocument || PASTED_SOURCE,
    }, fields.length);
    if (normalized) {
      fields.push(normalized);
    }
  });

  const scanStatus = mapParseStatus(doc);
  const hasPartialText = Boolean(String(doc.text_preview || pastedText || '').trim());
  const hasFields = fields.length > 0;

  if (!hasFields && hasPartialText && scanStatus !== EXTRACTION_STATUS.ERROR) {
    return {
      status: EXTRACTION_STATUS.PARTIAL,
      fields: [],
      clientData: null,
      textPreview: doc.text_preview || pastedText.slice(0, 500),
      warnings: doc.warnings || [],
      message: data?.message || PASTE_PARTIAL_TEXT_ONLY_MESSAGE,
      detail: EXTRACTION_PARTIAL_TITLE,
      pastedText,
    };
  }

  if (!hasFields && scanStatus === EXTRACTION_STATUS.ERROR) {
    return {
      status: EXTRACTION_STATUS.ERROR,
      fields: [],
      clientData: null,
      textPreview: doc.text_preview || null,
      warnings: doc.warnings || ['Could not parse structured fields from pasted text.'],
      message: data?.message || 'Could not parse pasted client info.',
      detail: null,
      pastedText,
    };
  }

  return {
    status: scanStatus === EXTRACTION_STATUS.EXTRACTED && !hasFields
      ? EXTRACTION_STATUS.PARTIAL
      : scanStatus,
    fields,
    clientData: clientDataFromReviewFields(fields, {}),
    textPreview: doc.text_preview || pastedText.slice(0, 500),
    warnings: doc.warnings || [],
    message: hasFields ? null : PASTE_PARTIAL_TEXT_ONLY_MESSAGE,
    detail: hasFields ? null : EXTRACTION_PARTIAL_TITLE,
    pastedText,
  };
}

export async function parsePastedClientInfo(text, options = {}) {
  const token = getAuthToken();
  const url = `${backendBaseUrl()}${PARSE_TEXT_PATH}`;

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        text,
        source: 'pasted_client_info',
        agency_id: options.agencyId || null,
        client_id: options.clientId || null,
      }),
    });
  } catch (error) {
    return {
      status: EXTRACTION_STATUS.ERROR,
      fields: [],
      clientData: null,
      message: 'Could not reach parsing service.',
      detail: error?.message || null,
      pastedText: text,
      warnings: [],
    };
  }

  const rawText = await response.text();
  let data = null;
  try {
    data = rawText ? JSON.parse(rawText) : null;
  } catch {
    data = { message: rawText };
  }

  if (!response.ok) {
    return {
      status: EXTRACTION_STATUS.ERROR,
      fields: [],
      clientData: null,
      message: data?.detail?.message || data?.detail || data?.message || 'Parse request failed.',
      pastedText: text,
      warnings: [],
    };
  }

  return normalizePastedParseResult(data, text);
}

export const PASTE_PLACEHOLDER = `Full Name: Marie Samuel
SSN: 123-45-6789
DOB: 11/05/1988
Home Address: 123 Main St, Orlando, FL 32801
Phone: +1 407 555 0198
Email: client@example.com
Employer: American Airlines
Occupation: Ramp Agent
Income: $50,000
Business Name: MS Beauty Studio LLC
EIN: 12-3456789
Business Address: 44 Market Plaza, Orlando, FL
Business Start Date: 03/13/2021`;

export { PASTED_SOURCE, EXTRACTION_PARTIAL_MANUAL_HINT };
